// One scan open for editing: its unedited build fully in memory (vertex order =
// chunk by chunk, each chunk level by level, as the viewer draws it), the
// pid of every vertex, the edit graph from the local server, and the state at
// HEAD. Every change is an op posted to the server, then a compare-and-swap of
// HEAD, then a local replay; or else staged (applied here only) and flushed later.
import { RECORD } from '../lod.ts';
import * as cache from '../cache.ts';
import type { CloudIndex, CloudMeta, OrigEntry, OrigFeatures, OrigImages, OrigLinks } from '../format.ts';
import { octDecode } from '../math.ts';
import { type Rigid, compose, identity, transformPoints } from '../align.ts';
import { type PidSet, encodeSet, decodeSet } from './pidset.ts';
import { type Op, type State, DELETED, apply, parseOp, replay, ancestors, children, emptyState, frameTransform, groupAndBelow } from './log.ts';
import { type PoseGraph, graphOf } from './springs.ts';
import { type CamImages, imagesOf } from './camimages.ts';
import { pidsOfWeights, weightsOfPids } from './weights.ts';
import { type Macro, type Step as MacroStep, dropGone } from './macros.ts';

export interface OpSummary {
  id: string; parent: string | null; t: string; op: Op['op']; msg?: string; n?: number;
  obj?: number; part?: number; cls?: string; name?: string; by?: string; rule?: unknown;
}
export interface Graph { head: string | null; refs: Record<string, string>; ops: OpSummary[]; n: number; stale: boolean }

/** Ops asked for in one request when a log is opened. */
const OPS_PER_READ = 500;

/** The local editor server (server/edit-server.ts). */
export class EditApi {
  root: string; top: string;
  /** root: one scene's API (api/scenes/<scene>/); top: the server's (deploy). Relative: the editor may live under a path (/edit/). */
  constructor(root = 'api/', top = 'api/') { this.root = root; this.top = top; }
  private async req<T>(path: string, init?: RequestInit, root = this.root): Promise<T> {
    const r = await fetch(root + path, init);
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(body.error || `HTTP ${r.status}`), { status: r.status, body });
    return body as T;
  }
  private write<T>(method: string, path: string, body: unknown, root = this.root) {
    return this.req<T>(path, { method, headers: { 'Content-Type': 'application/json', 'X-Cloudview-Edit': '1' }, body: JSON.stringify(body) }, root);
  }
  /** null when there is no editor server (the public static site). */
  async info(): Promise<{ scene?: string; title?: string; scans: { id: string; n: number; head: string | null; stale: boolean }[]; deploy?: boolean } | null> {
    try { return await this.req('info'); } catch { return null; }
  }
  graph = (scan: string) => this.req<Graph>(`scans/${encodeURIComponent(scan)}/graph`);
  /** An op never changes (its id is its hash): kept in the browser. */
  op = (scan: string, id: string) => cache.fetchJson<unknown>(this.opUrl(scan, id));
  opUrl = (scan: string, id: string) => this.root + `scans/${encodeURIComponent(scan)}/ops/${id}`;
  /** Many ops in one request (not kept: the caller keeps them under opUrl). */
  readOps = (scan: string, ids: string[]) => this.write<{ ops: Record<string, unknown> }>('POST', `scans/${encodeURIComponent(scan)}/ops/read`, { ids });
  post = (scan: string, op: unknown) => this.write<{ id: string }>('POST', `scans/${encodeURIComponent(scan)}/ops`, op);
  head = (scan: string, from: string | null, to: string | null, why: string) =>
    this.write<{ head: string | null }>('PUT', `scans/${encodeURIComponent(scan)}/head`, { from, to, why });
  ref = (scan: string, name: string, to: string | null) => this.write<Record<string, string>>('PUT', `scans/${encodeURIComponent(scan)}/refs/${encodeURIComponent(name)}`, { to });
  /** Publish; cancelled: stopped by cancelBuild (nothing published). */
  build = () => this.write<{ ok: boolean; log: string; cancelled?: boolean }>('POST', 'build', {});
  cancelBuild = () => this.write<{ cancelled: boolean }>('DELETE', 'build', {}).catch(() => ({ cancelled: false }));
  deploy = () => this.write<{ ok: boolean; log: string }>('POST', 'deploy', {}, this.top);
  /** The scan's saved point weights (weights.ts: float32 per pid), or null: none saved (or no editor server). Kept in the browser, fetched again only when changed. */
  weights = async (scan: string): Promise<Float32Array | null> => {
    const b = await cache.fetchTagged(this.weightsUrl(scan)).catch(() => null);
    return b && b.byteLength % 4 === 0 ? new Float32Array(b) : null;
  };
  saveWeights = async (scan: string, w: Float32Array) => {
    const url = this.weightsUrl(scan), r = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream', 'X-Cloudview-Edit': '1' }, body: new Blob([w as Float32Array<ArrayBuffer>]) });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
    if (typeof body.etag === 'string') await cache.putTagged(url, w.slice().buffer, body.etag);   // what a reload asks for: unchanged
  };
  private weightsUrl = (scan: string) => cache.abs(this.root + `scans/${encodeURIComponent(scan)}/weights`);
  /** The scene's macros (src/edit/macros.ts), without steps there are no more (dropped: what went, per macro). */
  macros = async () => dropGone((await this.req<{ macros: Macro[] }>('macros')).macros);
  saveMacro = async (name: string, steps: MacroStep[], vars?: Macro['vars'], graph?: Macro['graph']) => dropGone((await this.write<{ macros: Macro[] }>('PUT', `macros/${encodeURIComponent(name)}`, { steps, vars, ...(graph ? { graph } : {}) })).macros);
  dropMacro = async (name: string) => dropGone((await this.write<{ macros: Macro[] }>('DELETE', `macros/${encodeURIComponent(name)}`, {})).macros);
}

/** Op fields the client fills in (v, parent and t are added on commit). */
export type OpBody = Omit<Op, 'v' | 'parent' | 't'>;

/** An op staged in a LogSession: applied there, not yet in the log. */
export interface Staged { body: OpBody; why: string; t: string }

/** A HEAD moved (this page or another one editing the same scene): other pages reload that scan. */
export interface HeadMoved { root: string; scan: string; head: string | null }
const chan = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('cloudview-edit');
(chan as unknown as { unref?: () => void } | null)?.unref?.();   // node (tests): don't keep the process alive
/** Listen for HEAD moves made by other pages (a page doesn't hear its own). */
export function onHeadMoved(f: (m: HeadMoved) => void) { chan?.addEventListener('message', e => f(e.data as HeadMoved)); }

/**
 * One scan's edit log and its state at HEAD, without any geometry: what replay needs is N-sized arrays
 * of state only (see log.ts State), so a page can follow a scan's edits without holding its points.
 */
export class LogSession {
  graph!: Graph;
  state!: State;
  private ops = new Map<string, Op & { id: string }>();
  private redo: string[] = [];
  api: EditApi; scan: string;
  /** Source points (pids are 0..npid-1). */
  npid: number;

  protected constructor(api: EditApi, scan: string, npid: number) { this.api = api; this.scan = scan; this.npid = npid; }

  /** A scan's log, replayed to HEAD. */
  static async load(api: EditApi, scan: string, npid: number, onOps?: (n: number) => void): Promise<LogSession> {
    const s = new LogSession(api, scan, npid);
    s.onOps = onOps;
    await s.reload();
    s.onOps = undefined;
    return s;
  }

  /** While loading: called with how many more ops are in (from this browser or the server). */
  protected onOps?: (n: number) => void;

  get head() { return this.graph.head; }

  /** Called after every replay (the state may have changed). */
  protected onReplay() {}

  /** Fetch the graph and replay to HEAD. */
  async reload(): Promise<Staged[]> {
    this.graph = await this.api.graph(this.scan);
    return this.replayHead();
  }

  private async replayHead(): Promise<Staged[]> {
    const nodes = new Map(this.graph.ops.map(o => [o.id, o]));
    const path = ancestors(nodes, this.graph.head);
    // the ops kept in this browser, then the rest in a few requests (a long history is thousands of ops:
    // one request each takes minutes through a proxy)
    const todo = path.filter(id => !this.ops.has(id)), url = (id: string) => this.api.opUrl(this.scan, id);
    const kept = await Promise.all(todo.map(id => cache.get(url(id))));
    const need: string[] = [];
    todo.forEach((id, i) => {
      const b = kept[i];
      if (b) this.ops.set(id, { ...parseOp(JSON.parse(new TextDecoder().decode(b))), id });
      else need.push(id);
    });
    this.onOps?.(todo.length - need.length);
    for (let i = 0; i < need.length; i += OPS_PER_READ) {
      const ids = need.slice(i, i + OPS_PER_READ), { ops } = await this.api.readOps(this.scan, ids);
      for (const id of ids) {
        if (!ops[id]) throw new Error(`${this.scan}: op ${id} missing from the server's answer`);
        this.ops.set(id, { ...parseOp(ops[id]), id });
        void cache.put(url(id), new TextEncoder().encode(JSON.stringify(ops[id])));
      }
      this.onOps?.(ids.length);
    }
    return this.rebuildState();
  }

  /** The state at HEAD from the ops kept here (no requests), then the staged ops on it; those that no longer apply are dropped. */
  private rebuildState(): Staged[] {
    const path = ancestors(new Map(this.graph.ops.map(o => [o.id, o])), this.graph.head);
    this.state = path.length ? replay(this.npid, path.map(id => this.ops.get(id)!)) : emptyState(this.npid);
    this.stateAt = this.pending.length ? undefined : this.graph.head;
    const lost: Staged[] = [];
    this.pending = this.pending.filter(p => {
      try { apply(this.state, this.opOf(p)); return true; } catch { lost.push(p); return false; }
    });
    this.onReplay();
    return lost;
  }

  // ── staging: ops applied here only, until flush() writes them to the log ──
  /** Ops applied to the state here but not yet in the server's log (oldest first). */
  pending: Staged[] = [];
  get dirty() { return this.pending.length > 0; }

  private opOf(p: Staged): Op { return { v: 1, parent: null, t: p.t, ...p.body } as Op; }

  /** The HEAD the state is exactly the replay of (no staged ops on it); undefined: not known. */
  private stateAt: string | null | undefined;

  /** Apply an op here only (throws if it doesn't fit the state, which is then unchanged). */
  stage(body: OpBody, why: string = body.op, t = new Date().toISOString()) {
    const p = { body, why, t };
    try { apply(this.state, this.opOf(p)); } catch (e) { this.rebuildState(); throw e; }   // apply can throw midway
    this.pending.push(p);
    this.onReplay();
  }

  /** Take back the newest n staged ops. */
  unstage(n: number) {
    if (n <= 0) return;
    this.pending.splice(Math.max(0, this.pending.length - n));
    this.rebuildState();
  }

  /**
   * Write the staged ops to the log, chained after HEAD, then move HEAD once. If HEAD moved meanwhile
   * (409), they stay staged on the new HEAD (those that still apply) and the error is rethrown.
   */
  async flush(): Promise<Staged[]> {
    if (!this.pending.length) return [];
    const todo = this.pending.slice();
    let parent = this.graph.head;
    for (const p of todo) {
      const op = { ...this.opOf(p), parent }, { id } = await this.api.post(this.scan, op);
      this.ops.set(id, { ...parseOp(op), id });   // so the replay after the move needn't fetch it
      parent = id;
    }
    const keep = this.pending;
    this.pending = [];
    this.stateAt = undefined;   // the staged ops are in it: the replay after the move rebuilds it
    try {
      await this.move(parent, todo.length === 1 ? todo[0].why : `save ${todo.length} ops`);
    } catch (e) {
      if (this.graph.head === parent) throw e;   // HEAD did move: they're in the log
      this.pending = keep;
      if ((e as { status?: number }).status === 409) (e as { lost?: Staged[] }).lost = this.rebuildState();
      throw e;
    }
    this.redo = [];
    return todo;
  }

  /** Append an op after HEAD and move HEAD to it. */
  async commit(body: OpBody, why: string = body.op, t = new Date().toISOString()) {
    const op = { v: 1, parent: this.graph.head, t, ...body }, from = this.graph.head;
    const { id } = await this.api.post(this.scan, op);
    this.ops.set(id, { ...parseOp(op as Op), id });   // so the replay after the move needn't fetch it
    await this.move(id, why, from);
    this.redo = [];
    return id;
  }

  /**
   * Move HEAD (compare-and-swap); on a conflict reload and rethrow. from: HEAD was there and `to` is one op on it
   * (a commit): when the state is still that HEAD's, the op is applied to it, not the whole history replayed.
   */
  async move(to: string | null, why: string, from?: string | null) {
    try {
      await this.api.head(this.scan, this.graph.head, to, why);
    } catch (e) {
      if ((e as { status?: number }).status === 409) await this.reload();
      throw e;
    }
    chan?.postMessage({ root: this.api.root, scan: this.scan, head: to } satisfies HeadMoved);
    this.graph = await this.api.graph(this.scan);
    const op = to !== null && from !== undefined ? this.ops.get(to) : undefined;
    if (op && this.stateAt === from && op.parent === from && this.graph.head === to && !this.pending.length) {
      try {
        apply(this.state, op);
        this.stateAt = to;
        this.onReplay();
        return;
      } catch { /* it didn't fit (the state may be half changed): replay it all */ }
    }
    await this.replayHead();
  }

  async undo() {
    const h = this.graph.head;
    if (!h) return false;
    const parent = this.graph.ops.find(o => o.id === h)?.parent ?? null;
    await this.move(parent, 'undo');
    this.redo.push(h);
    return true;
  }

  /** Redo: the op undone last, else the newest child of HEAD. */
  async redoOne() {
    const kids = children(this.graph.ops).get(this.graph.head) ?? [];
    let to = this.redo.pop();
    if (!to || !kids.includes(to)) to = kids[kids.length - 1];
    if (!to) return false;
    const keep = this.redo;
    await this.move(to, 'redo');
    this.redo = keep;
    return true;
  }

  async checkout(id: string | null) { await this.move(id, 'checkout'); this.redo = []; }

  /** The HEAD path, root first. */
  path(): OpSummary[] {
    const nodes = new Map(this.graph.ops.map(o => [o.id, o]));
    return ancestors(nodes, this.graph.head).map(id => nodes.get(id)!);
  }
}

/** Per frame (in frameIds order), E(frame) from the alignment tree as 3 rows of (R | t), for the shader. */
export function frameMatsOf(tree: State['tree'], frameIds: number[]) {
  const M = new Float32Array(12 * frameIds.length);
  frameIds.forEach((f, k) => {
    const E = frameTransform(tree, f), R = E.R, t = E.t;
    M.set([R[0], R[1], R[2], t[0], R[3], R[4], R[5], t[1], R[6], R[7], R[8], t[2]], 12 * k);
  });
  return M;
}

/** World copies of points (file frame): each through its frame's E (frameMats, by frameIx), then the scan's alignment T. */
export function worldOf(xyz: Float32Array, nrm: Float32Array, frameIx: Uint16Array, frameMats: Float32Array, T: Rigid,
  P: Float32Array = new Float32Array(xyz.length), N: Float32Array = new Float32Array(nrm.length)) {
  const M = frameMats, nf = M.length / 12, W: Rigid[] = [];
  for (let k = 0; k < nf; k++) {
    const o = 12 * k;
    W.push(compose(T, { R: [M[o], M[o + 1], M[o + 2], M[o + 4], M[o + 5], M[o + 6], M[o + 8], M[o + 9], M[o + 10]], t: [M[o + 3], M[o + 7], M[o + 11]] }));
  }
  const x = xyz, n = nrm, ix = frameIx, nv = xyz.length / 3;
  for (let v = 0; v < nv; v++) {
    const R = W[ix[v]].R, t = W[ix[v]].t, i = 3 * v;
    const a = x[i], b = x[i + 1], c = x[i + 2];
    P[i] = R[0] * a + R[1] * b + R[2] * c + t[0]; P[i + 1] = R[3] * a + R[4] * b + R[5] * c + t[1]; P[i + 2] = R[6] * a + R[7] * b + R[8] * c + t[2];
    const d = n[i], e = n[i + 1], f = n[i + 2];
    N[i] = R[0] * d + R[1] * e + R[2] * f; N[i + 1] = R[3] * d + R[4] * e + R[5] * f; N[i + 2] = R[6] * d + R[7] * e + R[8] * f;
  }
  return { P, N };
}

export class EditSession extends LogSession {
  /** Per vertex, in the scan's own (file) frame. */
  xyz!: Float32Array;
  nrm!: Float32Array;          // NaN where the point has no normal
  rgb!: Uint8Array;            // per vertex, 3 bytes
  frame: Uint32Array | null = null;
  /** Per vertex, the capture range byte (RANGE_STEP m a unit, 0 unknown); null in a version 1 build (none recorded). */
  range: Uint8Array | null = null;
  /** Per vertex, the depth-image edge byte (prep/edges.py; 0 unknown); null in a version 1 build. */
  edge: Uint8Array | null = null;
  /** The edge bytes have the side (bit 2), for signed Edges selections (the index's edgeSign). */
  edgeSign = false;
  /** The scan's frame ids (sorted; [0] without frame ids) and each vertex's index into them. */
  frameIds: number[] = [0];
  frameIx!: Uint16Array;
  /** Per frame index, E(frame) from the alignment tree as 3 rows of (R | t), for the shader. */
  frameMats = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0]);
  /** Bumped whenever the tree moves points (world copies and grids are then stale). */
  treeVersion = 0;
  pidOfV!: Uint32Array;
  vOfPid!: Int32Array;         // -1: pid not in the build (non-finite in the source)
  recs: Uint8Array[] = [];     // per chunk, vertex order
  nv = 0;
  /** World frame copies (T applied), what the selection tools work on. */
  P!: Float32Array;
  N!: Float32Array;
  T: Rigid = identity();
  meta: CloudMeta; idx: CloudIndex;
  /** RTAB-Map's links between the frames, with their poses (file frame), if the build has them. */
  links: PoseGraph | null = null;
  /** Each frame's camera image and pose (file frame), if the build has them. */
  images: CamImages | null = null;
  /** RTAB-Map's keypoints and words (prep/features.py): Align cameras checks itself by them. */
  features: OrigFeatures | null = null;
  /** Per vertex, its weight (weights.ts; 1 unless set), and the scan's weights as last saved (per pid; null: none). */
  weight!: Float32Array;
  savedWeights: Float32Array | null = null;

  private constructor(api: EditApi, meta: CloudMeta, idx: CloudIndex, npid: number) {
    super(api, meta.id, npid);
    this.meta = meta; this.idx = idx;
  }

  static async open(api: EditApi, base: string, meta: CloudMeta, T: Rigid): Promise<EditSession> {
    const man = await cache.fetchFreshJson<Record<string, OrigEntry>>(base + 'orig/manifest.json');
    const e = man[meta.id];
    if (!e) throw new Error(`${meta.id} has no unedited build in orig/ (rebuild with prep/build_tiles.py)`);
    const get = (f: string) => cache.fetchBuf(base + 'orig/' + f);   // hashed names: kept in the browser
    cache.prune(base + 'orig/', new Set(Object.values(man).flatMap(x => [x.index, x.bin, x.pid, ...(x.frame ? [x.frame] : []), ...(x.links ? [x.links] : []), ...(x.images ? [x.images, x.images.replace(/\.images\.json$/, '.img/')] : []), ...(x.features ? [x.features] : [])]).map(f => base + 'orig/' + f)));
    const [idx, bin, pid, frame] = await Promise.all([
      get(e.index).then(b => JSON.parse(new TextDecoder().decode(b)) as CloudIndex), get(e.bin), get(e.pid), e.frame ? get(e.frame) : Promise.resolve(null)]);
    const s = new EditSession(api, meta, idx, e.n);
    s.build(bin, new Uint32Array(pid), frame ? new Uint32Array(frame) : null);
    // the pose graph is for the scan path and frame springs: the scan edits without it
    if (e.links) s.links = await get(e.links).then(b => graphOf(JSON.parse(new TextDecoder().decode(b)) as OrigLinks), err => { console.warn(`${meta.id}: no pose graph (${err.message})`); return null; });
    if (e.features) s.features = await get(e.features).then(b => JSON.parse(new TextDecoder().decode(b)) as OrigFeatures, err => { console.warn(`${meta.id}: no RTAB-Map features (${err.message})`); return null; });
    if (e.images) s.images = await get(e.images).then(b => imagesOf(JSON.parse(new TextDecoder().decode(b)) as OrigImages, base + 'orig/', e.images!), err => { console.warn(`${meta.id}: no camera images (${err.message})`); return null; });
    s.savedWeights = await api.weights(meta.id).then(w => (w && w.length === s.npid ? w : null));
    s.weight = weightsOfPids(s.savedWeights, s.pidOfV);
    s.T = T;
    s.frameMats = new Float32Array(12 * s.frameIds.length);   // forces the first treeChanged() to fill it
    await s.reload();
    return s;
  }

  /** Records -> vertex-order arrays. */
  private build(bin: ArrayBuffer, pidRec: Uint32Array, frameRec: Uint32Array | null) {
    // the file's records may be a version 1 build's 12 bytes (no capture range): the GPU copies are RECORD each
    const ch = this.idx.chunks, rs = this.idx.record, nv = bin.byteLength / rs;
    if (pidRec.length !== nv) throw new Error(`pid sidecar has ${pidRec.length} entries for ${nv} points`);
    this.nv = nv;
    this.xyz = new Float32Array(3 * nv); this.nrm = new Float32Array(3 * nv); this.rgb = new Uint8Array(3 * nv);
    this.pidOfV = new Uint32Array(nv); this.vOfPid = new Int32Array(this.npid).fill(-1);
    this.frame = frameRec ? new Uint32Array(nv) : null;
    this.range = rs > 12 ? new Uint8Array(nv) : null;
    this.edge = rs > 13 ? new Uint8Array(nv) : null;
    this.edgeSign = !!this.idx.edgeSign;
    const d = new DataView(bin), u8 = new Uint8Array(bin);
    let v = 0;
    for (const c of ch) {
      const rec = new Uint8Array(c.levelCounts.reduce((a, b) => a + b, 0) * RECORD);
      let o = 0;
      c.levelCounts.forEach((n, l) => {
        const r0 = c.offsets[l] / rs;
        if (rs === RECORD) rec.set(u8.subarray(c.offsets[l], c.offsets[l] + n * rs), o);
        else for (let j = 0; j < n; j++) rec.set(u8.subarray(c.offsets[l] + j * rs, c.offsets[l] + j * rs + Math.min(rs, RECORD)), o + j * RECORD);
        o += n * RECORD;
        for (let j = 0; j < n; j++, v++) {
          const r = r0 + j, b = r * rs;
          for (let k = 0; k < 3; k++) this.xyz[3 * v + k] = c.origin[k] + (d.getUint16(b + 2 * k, true) / 65535) * c.size[k];
          this.rgb[3 * v] = u8[b + 6]; this.rgb[3 * v + 1] = u8[b + 7]; this.rgb[3 * v + 2] = u8[b + 8];
          if (u8[b + 9] & 1) {
            const n3 = octDecode(u8[b + 10], u8[b + 11]);
            this.nrm[3 * v] = n3[0]; this.nrm[3 * v + 1] = n3[1]; this.nrm[3 * v + 2] = n3[2];
          } else this.nrm[3 * v] = this.nrm[3 * v + 1] = this.nrm[3 * v + 2] = NaN;
          const p = pidRec[r];
          if (p >= this.npid) throw new Error(`pid ${p} >= ${this.npid}`);
          this.pidOfV[v] = p; this.vOfPid[p] = v;
          if (frameRec && this.frame) this.frame[v] = frameRec[r];
          if (this.range) this.range[v] = u8[b + 12];
          if (this.edge) this.edge[v] = u8[b + 13];
        }
      });
      this.recs.push(rec);
    }
    this.frameIx = new Uint16Array(nv);
    if (this.frame) {
      this.frameIds = [...new Set(this.frame)].sort((a, b) => a - b);
      if (this.frameIds.length > 65535) throw new Error(`${this.frameIds.length} frames: too many for the editor`);
      const ix = new Map(this.frameIds.map((f, i) => [f, i]));
      for (let v = 0; v < nv; v++) this.frameIx[v] = ix.get(this.frame[v])!;
    }
  }

  /** Save the weights (keeping what was saved for points not in this build). */
  async saveWeights() {
    const w = pidsOfWeights(this.weight, this.pidOfV, this.npid, this.savedWeights);
    await this.api.saveWeights(this.scan, w);
    this.savedWeights = w;
  }

  setTransform(T: Rigid) {
    this.T = T;
    this.world();
  }

  /** World copies: each vertex through its frame's E, then the scan's alignment T. */
  private world() {
    if (!this.P) { this.P = new Float32Array(3 * this.nv); this.N = new Float32Array(3 * this.nv); }
    worldOf(this.xyz, this.nrm, this.frameIx, this.frameMats, this.T, this.P, this.N);
  }

  protected override onReplay() { this.treeChanged(); }

  /** Recompute frameMats from the state's tree; true (and world copies redone) if anything moved. */
  private treeChanged() {
    const M = frameMatsOf(this.state.tree, this.frameIds);
    if (M.length === this.frameMats.length && M.every((x, i) => x === this.frameMats[i])) return false;
    this.frameMats = M;
    this.treeVersion++;
    this.world();
    return true;
  }

  // ── masks ─────────────────────────────────────────────────────────────
  /** Vertex mask -> the pid set an op carries. */
  toSet(m: Uint8Array): PidSet {
    const pids: number[] = [];
    for (let v = 0; v < m.length; v++) if (m[v]) pids.push(this.pidOfV[v]);
    pids.sort((a, b) => a - b);
    return encodeSet(pids);
  }

  /** Vertex mask from a per-pid predicate on the current state. */
  where(f: (s: State, pid: number) => boolean): Uint8Array {
    const m = new Uint8Array(this.nv), s = this.state;
    for (let v = 0; v < this.nv; v++) m[v] = f(s, this.pidOfV[v]) ? 1 : 0;
    return m;
  }

  /** Vertex mask of some frames. */
  framesMask(frames: Set<number>): Uint8Array {
    const m = new Uint8Array(this.nv), f = this.frame;
    if (f) for (let v = 0; v < this.nv; v++) m[v] = frames.has(f[v]) ? 1 : 0;
    return m;
  }

  /** Vertex mask of a saved group and every group below it. */
  groupDeep(name: string): Uint8Array {
    const m = this.group(name);
    for (const g of groupAndBelow(this.state, name).slice(1)) { const x = this.group(g); for (let v = 0; v < m.length; v++) m[v] |= x[v]; }
    return m;
  }

  /** Vertex mask of a saved group (its own points). */
  group(name: string): Uint8Array {
    const g = this.state.groups.get(name), m = new Uint8Array(this.nv);
    if (!g) return m;
    const pids = decodeSet(g, this.npid);
    for (const p of pids) { const v = this.vOfPid[p]; if (v >= 0) m[v] = 1; }
    return m;
  }

  /** Per-vertex word for the shader's aEdit (see shaders.ts); clsIx maps this scan's class ids to the tint table's. */
  words(sel: Uint8Array | null | undefined, cand: Uint8Array | null | undefined, hide: Uint8Array | null | undefined, clsIx: Uint8Array): Uint32Array {
    const s = this.state, out = new Uint32Array(this.nv);
    for (let v = 0; v < this.nv; v++) {
      const p = this.pidOfV[v], f = s.flags[p], o = s.obj[p], pt = s.part[p];
      let w = (f & DELETED) | (clsIx[f >> 1] << 16);
      if (o) w |= 32 | ((o & 255) << 8);
      else if (pt) w |= 64 | ((pt & 255) << 8);
      if (sel?.[v]) w |= 2;
      if (cand?.[v]) w |= 4;
      if (hide?.[v]) w |= 128;
      out[v] = w;
    }
    return out;
  }
}
